
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Shield } from "lucide-react";
import { Link } from "react-router-dom";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import DynamicContactSection from "@/components/legal/DynamicContactSection";

const Privacy = () => {
  const { data: settings } = useSiteSettings();

  // Get dynamic values with fallbacks
  const siteTitle = settings?.site_title || "Bobo & Teedee®";
  const legalEmail = settings?.legal_email || "privacy@boboteedee.ky";
  const legalPhone = settings?.legal_phone || "+1 (345) 123-4567";
  const legalAddress = settings?.legal_address || "Bobo & Teedee® Privacy Department, Grand Cayman, Cayman Islands";
  
  // Fix effective date formatting to avoid timezone issues
  const effectiveDate = settings?.privacy_effective_date 
    ? (() => {
        // Create date object and parse it as local date to avoid timezone shifts
        const dateStr = settings.privacy_effective_date;
        const [year, month, day] = dateStr.split('-').map(Number);
        const localDate = new Date(year, month - 1, day); // month is 0-indexed
        return localDate.toLocaleDateString('en-US', { 
          year: 'numeric', 
          month: 'long',
          day: 'numeric'
        });
      })()
    : "January 1, 2025";

  // Get copyright year range
  const currentYear = new Date().getFullYear();
  const startYear = settings?.copyright_start_year || currentYear;
  const copyrightYear = startYear === currentYear ? currentYear : `${startYear}-${currentYear}`;

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-blue/20 via-white to-bobo-green/20">
      {/* Header */}
      <section className="py-16 bg-gradient-to-r from-bobo-green to-brand-purple text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center mb-6">
            <Button asChild variant="ghost" className="text-white hover:bg-white/20 mr-4">
              <Link to="/">
                <ArrowLeft className="h-5 w-5 mr-2" />
                Back to Home
              </Link>
            </Button>
          </div>
          <div className="text-center">
            <div className="flex justify-center mb-4">
              <Shield className="h-16 w-16" />
            </div>
            <h1 className="text-5xl font-bold mb-6">Privacy Policy</h1>
            <p className="text-xl max-w-3xl mx-auto opacity-90">
              Your privacy and your child's safety are our top priorities.
            </p>
            <p className="text-sm mt-4 opacity-75">Last updated: {effectiveDate}</p>
          </div>
        </div>
      </section>

      {/* Privacy Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Card className="shadow-xl bg-white/90 backdrop-blur-sm">
          <CardHeader>
            <CardTitle className="text-2xl text-bobo-green">Privacy Policy for {siteTitle}</CardTitle>
          </CardHeader>
          <CardContent className="prose prose-lg max-w-none space-y-8">
            <section>
              <h2 className="text-xl font-bold text-bobo-green mb-4">1. Our Commitment to Privacy</h2>
              <p className="text-gray-700 leading-relaxed">
                {siteTitle} is committed to protecting the privacy and safety of our users, especially children. This Privacy Policy explains how we collect, use, and protect information when you use our website, mobile applications, and services.
              </p>
              <p className="text-gray-700 leading-relaxed">
                We comply with the Children's Online Privacy Protection Act (COPPA) and other applicable privacy laws to ensure the safety of children online.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-teedee-blue mb-4">2. Information We Collect</h2>
              
              <h3 className="text-lg font-semibold text-teedee-blue mb-3">Information from Adults (Parents, Teachers, Guardians)</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Name and email address for newsletter subscriptions</li>
                <li>Contact information when you reach out to us</li>
                <li>Feedback and comments about our content</li>
                <li>Account information if you create a user account</li>
              </ul>

              <h3 className="text-lg font-semibold text-teedee-blue mb-3 mt-6">Information from Children Under 13</h3>
              <p className="text-gray-700 leading-relaxed">
                We do not knowingly collect personal information from children under 13 without verified parental consent. Any information collected from children is limited to:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Usage data to improve our educational content (anonymized)</li>
                <li>Technical information necessary for the service to function</li>
                <li>Information with explicit parental consent only</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold text-brand-purple mb-4">3. How We Use Information</h2>
              <p className="text-gray-700 leading-relaxed mb-4">We use collected information to:</p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Provide and improve our educational content and services</li>
                <li>Send newsletters and updates about new episodes and activities</li>
                <li>Respond to questions and provide customer support</li>
                <li>Analyze usage patterns to create better educational experiences</li>
                <li>Ensure the safety and security of our platform</li>
                <li>Comply with legal obligations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold text-golden-orange mb-4">4. Information Sharing and Disclosure</h2>
              <p className="text-gray-700 leading-relaxed">
                We do not sell, trade, or rent personal information to third parties. We may share information only in the following circumstances:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li><strong>Service Providers:</strong> Trusted partners who help us operate our services (e.g., email service providers, hosting services)</li>
                <li><strong>Legal Requirements:</strong> When required by law or to protect the safety of our users</li>
                <li><strong>Parental Requests:</strong> Information about a child's use of our services, upon verified parental request</li>
                <li><strong>Business Transfers:</strong> In the event of a merger or acquisition (with continued privacy protection)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold text-teedee-blue mb-4">5. Parental Rights and Control</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Parents and guardians have the following rights regarding their child's information:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Review any personal information we have collected about their child</li>
                <li>Request deletion of their child's personal information</li>
                <li>Refuse to allow further collection of their child's information</li>
                <li>Request details about how we use their child's information</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                To exercise these rights, please contact us at {legalEmail} with verification of your parental status.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-bobo-green mb-4">6. Data Security</h2>
              <p className="text-gray-700 leading-relaxed">
                We implement appropriate security measures to protect personal information against unauthorized access, alteration, disclosure, or destruction. These measures include:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Encryption of sensitive data in transit and at rest</li>
                <li>Regular security assessments and updates</li>
                <li>Limited access to personal information on a need-to-know basis</li>
                <li>Employee training on privacy and security practices</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold text-brand-purple mb-4">7. Cookies and Tracking Technologies</h2>
              <p className="text-gray-700 leading-relaxed">
                We use cookies and similar technologies to improve user experience and analyze usage patterns. These technologies help us:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Remember user preferences and settings</li>
                <li>Understand how our content is being used</li>
                <li>Improve the performance of our website and applications</li>
                <li>Provide relevant content recommendations</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                You can control cookies through your browser settings. Please see our Cookie Policy for more details.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-golden-orange mb-4">8. Third-Party Services</h2>
              <p className="text-gray-700 leading-relaxed">
                Our platform integrates with third-party services such as:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li><strong>YouTube:</strong> For hosting and displaying our educational videos</li>
                <li><strong>Social Media Platforms:</strong> For sharing content and community engagement</li>
                <li><strong>Analytics Services:</strong> To understand usage and improve our content</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                These services have their own privacy policies, and we encourage you to review them.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-teedee-blue mb-4">9. International Data Transfers</h2>
              <p className="text-gray-700 leading-relaxed">
                As a Cayman Islands-based service, your information may be processed in the Cayman Islands or other countries where our service providers operate. We ensure appropriate safeguards are in place for international data transfers.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-bobo-green mb-4">10. Data Retention</h2>
              <p className="text-gray-700 leading-relaxed">
                We retain personal information only as long as necessary to provide our services and fulfill the purposes described in this policy. When information is no longer needed, we securely delete or anonymize it.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-brand-purple mb-4">11. Changes to This Privacy Policy</h2>
              <p className="text-gray-700 leading-relaxed">
                We may update this Privacy Policy from time to time. Significant changes will be communicated through our website, email notifications, or other appropriate means. We will also update the "Last Updated" date at the top of this policy.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-golden-orange mb-4">12. Contact Us</h2>
              <DynamicContactSection title="this Privacy Policy or our privacy practices" />
            </section>

            <div className="border-t pt-8 mt-12">
              <p className="text-sm text-gray-500 text-center">
                © {copyrightYear} {siteTitle}. All rights reserved.<br />
                This Privacy Policy is effective as of {effectiveDate}.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Privacy;
